import React from 'react';
import { Button, ConfigProvider } from 'antd';
import classes from './YandexButton.module.scss';
import Assets from 'assets/icons';
import colors from 'scss/variables/colors.module.scss';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { useTranslation } from 'react-i18next';
import Spinner from 'components/Spinner/Spinner';
import { athena_state, generateCodeChallenge, getDeviceIdForAuth } from 'helpers/oauth2';
import { setUnlinkModalState } from 'store/slices/windowStateSlice';

const client_id = window.REACT_APP_YA_CLIENT_ID !== 'REPLACE_REACT_APP_YA_CLIENT_ID' ? window.REACT_APP_YA_CLIENT_ID : process.env.REACT_APP_YA_CLIENT_ID;

type YandexButtonProps = {
  action: string;
  loading?: boolean;
  preLogic?: () => Promise<boolean>;
}

const YandexButton = ({
  action,
  loading,
  preLogic,
}: YandexButtonProps) => {
  const [requesting, setRequesting] = React.useState(false);

  const { messageApi } = useAppSelector((state) => state.windowState);
  
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const returnUri = {
    auth:`${window.location.origin}/ya-${action}`,
    register: `${window.location.origin}/ya-${action}`,
    add: window.location.origin,
  }
  
  const yaLogin = async () => {
    const conditionFulfilled = preLogic ? await preLogic() : true;    
    if (conditionFulfilled) {
      setRequesting(true);
      try {
        const code_verifier = athena_state + '_' + Date.now();
        localStorage.setItem('code_verifier', code_verifier);
        const code_challenge = await generateCodeChallenge(code_verifier);
        const redirect_uri = returnUri[action as keyof typeof returnUri];
        const device_id = await getDeviceIdForAuth();
        const device_name = `browser_${athena_state}`;
        const scope = 'login:avatar+login:email+login:info+login:default_phone';
        const queryParams = `?response_type=code&force_confirm=true&client_id=${client_id}&redirect_uri=${redirect_uri}&device_name=${device_name}&state=${athena_state}&device_id=${device_id}&code_challenge=${code_challenge}&code_challenge_method=S256&scope=${scope}`;
        window.location.assign('https://oauth.yandex.ru/authorize' + queryParams);
      } catch {
        messageApi?.error(t('login.external.errorRequestAuth', {name: t('login.external.btns.yandex')}));
        setRequesting(false);
        localStorage.removeItem('code_verifier');
      }
    }
  };

  const handlePageShow = (event: PageTransitionEvent) => {
    if (event.persisted || window.performance.navigation.type === 2) setRequesting(false);
  };

  React.useEffect(() => {
    window.addEventListener('pageshow', handlePageShow);
    return () => {
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, []);

  const deleteExternalAuthModal = () => {
    dispatch(setUnlinkModalState({open: true, service_name: 'yandex', in_progress: false}));
  };

  const btnText = {
    auth: t('login.external.btns.login', {name: t('login.external.btns.yandex')}),
    register: t('login.external.btns.register', {name: t('login.external.btns.yandex')}),
    add: t('login.external.btns.register', {name: t('login.external.btns.yandex')}),
    delete: t('login.external.btns.register', {name: t('login.external.btns.yandex')}),
  };

  const onButtonClick = {
    auth: () => yaLogin(),
    register: () => yaLogin(),
    add: () => yaLogin(),
    delete: () => deleteExternalAuthModal(),
  };

  return (
    <ConfigProvider theme={{ token: { colorPrimary: colors.black, colorPrimaryHover: colors.black }}}>
      <Button
        type='primary'
        onClick={onButtonClick[action as keyof typeof onButtonClick]}
        className={classes.yaBtn}
        loading={loading || requesting}
      >
        <Assets.YandexIcon className={classes.yaIcon}/>
        {btnText[action as keyof typeof btnText]}
        {(loading || requesting) && <Spinner height={'26px'} width={'26px'} color={'white'} size={'22px'}/>}
      </Button>
    </ConfigProvider>
  );
}

export default YandexButton;
